import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Checkbox } from "@shared/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Search } from "lucide-react";
import React, { useState } from "react";
import { UseFormReturn } from "react-hook-form";
export const NetworkSearchModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  facilityName: string;
  onConnectNetworks: (networkIds: string[]) => void;
}> = ({ isOpen, onClose, facilityName, onConnectNetworks }) => {
  const [selectedNetworks, setSelectedNetworks] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const mockNetworks = [
    {
      id: "NET-201",
      name: "Southern Bypass Interceptor",
      type: "Sewer Main",
      serviceZone: "Southern Division",
    },
    {
      id: "NET-202",
      name: "Airport Trunk Line",
      type: "Water Main",
      serviceZone: "Southern Division",
    },
    {
      id: "NET-203",
      name: "Central Force Main",
      type: "Force Main",
      serviceZone: "Central Division",
    },
    {
      id: "NET-204",
      name: "Eastside Distribution Network",
      type: "Water Main",
      serviceZone: "Eastern Division",
    },
    {
      id: "NET-205",
      name: "Westbank Collector System",
      type: "Sewer Main",
      serviceZone: "Western Division",
    },
  ];

  const searchFilterFields: FormField[] = [
    {
      name: "networkType",
      type: "select",
      label: "Network Type",
      placeholder: "All Types",
      options: [
        { label: "All Types", value: "" },
        { label: "Water Main", value: "Water Main" },
        { label: "Sewer Main", value: "Sewer Main" },
        { label: "Force Main", value: "Force Main" },
      ],
      defaultValue: "",
      classes: {
        container: "w-full",
        select: "w-full h-10 px-3 border border-gray-300 rounded-lg",
      },
    },
    {
      name: "serviceZone",
      type: "select",
      label: "Service Zone",
      placeholder: "All Zones",
      options: [
        { label: "All Zones", value: "" },
        { label: "Northern Division", value: "Northern Division" },
        { label: "Southern Division", value: "Southern Division" },
        { label: "Eastern Division", value: "Eastern Division" },
        { label: "Western Division", value: "Western Division" },
        { label: "Central Division", value: "Central Division" },
      ],
      defaultValue: "",
      classes: {
        container: "w-full",
        select: "w-full h-10 px-3 border border-gray-300 rounded-lg",
      },
    },
  ];

  const searchFilterForm: UseFormReturn<any> = FormService.createForm<any>({
    fields: searchFilterFields,
    defaultValues: {
      networkType: "",
      serviceZone: "",
    },
  });

  const handleSearchFilterSubmit = (data: any) => {};

  const handleNetworkToggle = (networkId: string) => {
    setSelectedNetworks((prev) =>
      prev.includes(networkId)
        ? prev.filter((id) => id !== networkId)
        : [...prev, networkId]
    );
  };

  const handleConnectSelected = () => {
    if (selectedNetworks.length > 0) {
      onConnectNetworks(selectedNetworks);
      setSelectedNetworks([]);
      setSearchTerm("");
    }
  };

  const handleClose = () => {
    setSelectedNetworks([]);
    setSearchTerm("");
    onClose();
  };

  const filteredNetworks = mockNetworks.filter((network) => {
    const matchesSearch =
      network.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      network.id.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Connect Networks to {facilityName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by network name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <DynamicForm
            fields={searchFilterFields}
            form={searchFilterForm}
            onSubmit={handleSearchFilterSubmit}
            gridColumns={2}
            responsiveColumns={{ sm: 1, md: 2 }}
            config={{
              showProgress: false,
              layout: "vertical",
              showSubmitButton: false,
              showResetButton: false,
            }}
          />

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-900">
                Available Networks ({filteredNetworks.length})
              </h3>
              {selectedNetworks.length > 0 && (
                <Badge
                  variant="secondary"
                  className="bg-blue-100 text-blue-800"
                >
                  {selectedNetworks.length} selected
                </Badge>
              )}
            </div>

            <div className="border rounded-lg max-h-96 overflow-y-auto">
              {filteredNetworks.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No networks found matching your criteria.
                </div>
              ) : (
                <div className="divide-y">
                  {filteredNetworks.map((network) => (
                    <div
                      key={network.id}
                      className="flex items-center gap-4 p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleNetworkToggle(network.id)}
                    >
                      <Checkbox
                        id={network.id}
                        checked={selectedNetworks.includes(network.id)}
                        onCheckedChange={() => handleNetworkToggle(network.id)}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <label
                            htmlFor={network.id}
                            className="font-medium text-gray-900 cursor-pointer"
                          >
                            {network.name}
                          </label>
                          <Badge variant="secondary">{network.type}</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>ID: {network.id}</span>
                          <span>Zone: {network.serviceZone}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button
              onClick={handleConnectSelected}
              disabled={selectedNetworks.length === 0}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              Connect Selected ({selectedNetworks.length})
            </Button>
            <Button variant="outline" onClick={handleClose} className="px-8">
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
